import { Box, Container, Flex, Image, Text } from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import { motion } from "framer-motion"

import discordLogoWhite from "#/assets/discord-logo-white.svg"
import mezoLogoWhite from "#/assets/mezo-logo-inline-white.svg"
import redditLogoWhite from "#/assets/reddit-logo-white.svg"
import telegramLogoWhite from "#/assets/telegram-logo-white.svg"
import xLogoWhite from "#/assets/x-logo-white.svg"
import { LANDING_CONTAINER_WIDTH, links } from "#/shared/constants"

type FooterNavLink = {
  title: string
  url: string
  linkTarget: boolean
}

type FooterNavSection = {
  title: string
  dropdownItems?: FooterNavLink[]
}

type FooterSectionProps = {
  footerNav?: FooterNavSection[]
}

export default function LandingFooter({ footerNav = [] }: FooterSectionProps) {
  return (
    <>
      <style>{`
      .nav-link {
        font-size: 36px;
        font-weight: 600;
        font-family: riforma.medium
        line-height: 40px;
        width: 100%;
        letter-spacing: -1px;
        display: block;
        margin: 0px 0;
        text-transform: none;
        color: white;
      }

      .nav-link:hover, .social-icons img:hover {
        border: none;
        opacity: 0.5;
        transition: all 0.2s ease-in-out;
      }
    `}</style>
      <motion.div
        initial="hidden"
        transition={{ type: "spring", stiffness: 100, damping: 15 }}
        whileInView="visible"
        viewport={{ once: true, amount: 0.0 }}
        variants={{
          hidden: { y: 150 },
          visible: { y: 0 },
        }}
      >
        <Box
          w="100%"
          background="#000000"
          mt="-150px"
          h={{ base: "auto", md: "auto" }}
          p={{ base: "30px", md: "60px 60px" }}
          position="relative"
          color="primary.white.100"
        >
          <Container maxW={LANDING_CONTAINER_WIDTH} position="relative">
            <Image
              src={mezoLogoWhite}
              mt={{ base: "50px", md: "40px" }}
              display={{ base: "block", md: "block" }}
            />
            <Box
              width={{ base: "100%" }}
              color="primary.white.100"
              display={{ base: "block", md: "none" }}
            />
            <Text
              fontSize={{ base: "16px", md: "18px" }}
              lineHeight="130%"
              fontFamily="riforma.regular"
              maxWidth={{ base: "100%", md: "30%" }}
              color="primary.white.50"
              m="40px 0"
              pb={{ base: "40px", md: "0px" }}
              mb={{ base: "0px", md: "0px" }}
              borderBottom={{ base: "1px solid white", md: "none" }}
            >
              Mezo is built by Thesis, a team that has dedicated 10+ years to
              expanding Bitcoin with security as top priority. Creators of tBTC,
              Fold, Taho, and more.
            </Text>
            <Flex mt="40px" className="social-icons">
              <Link to={links.X}>
                <Image src={xLogoWhite} alt="X Logo" marginRight="5" />
              </Link>
              <Link to={links.DISCORD}>
                <Image src={discordLogoWhite} alt="Discord Logo" />
              </Link>
              <Link to={links.TELEGRAM}>
                <Image
                  src={telegramLogoWhite}
                  alt="Telegram Logo"
                  marginLeft="5"
                />
              </Link>
              <Link to={links.REDDIT}>
                <Image
                  src={redditLogoWhite}
                  alt="Reddit Logo"
                  marginLeft="5"
                  marginTop="-2px"
                />
              </Link>
            </Flex>
            <Box
              position={{ base: "relative", md: "absolute" }}
              mt={{ base: "40px", md: 0 }}
              right={{ base: "10%", sm: "30px", md: "0px" }}
              padding={{ base: "0 30px", md: 0 }}
              top={{ base: "80%", md: "30px" }}
              width={{ base: "100%", md: "60%" }}
              color="primary.white.100"
              mb={{ base: "40px", md: "0" }}
            >
              {footerNav && (
                <Flex
                  justify="space-around"
                  flexDirection={{ base: "column", md: "row" }}
                  flexWrap="wrap"
                  gap={0}
                  fontFamily="riforma.medium"
                >
                  {footerNav?.map((section) => (
                    <Box key={section.title} mb={8}>
                      <Text
                        fontSize="18px"
                        fontWeight="bold"
                        mb={4}
                        color="primary.white.100"
                      >
                        {section.title}
                      </Text>
                      <Flex direction="column" gap={2}>
                        {section.dropdownItems?.map((item) => (
                          <Link
                            key={item.title}
                            to={item.url}
                            target={item.linkTarget ? "_blank" : undefined}
                            className={`nav-link footer-${item.title.toLowerCase()}`}
                            style={{
                              fontSize: "18px",
                              color: "#626262",
                              fontWeight: 500,
                              textTransform: "none",
                            }}
                          >
                            {item.title}
                          </Link>
                        ))}
                      </Flex>
                    </Box>
                  ))}
                </Flex>
              )}
            </Box>
            <Box
              w="100%"
              borderTop="1px solid rgb(63, 63, 63)"
              borderBottom="1px solid rgb(63, 63, 63)"
              p="40px 0"
              m="80px 0"
              color="primary.white.50"
            >
              <Flex justifyContent="space-between">
                <Text fontSize="18px">© {new Date().getFullYear()} Mezo</Text>
                <Text fontSize="18px">
                  <a href="/terms">Terms</a> &nbsp;
                  <a href="/privacy">Privacy</a>
                </Text>
              </Flex>
            </Box>
          </Container>
        </Box>
      </motion.div>
    </>
  )
}
